import React, { useState } from 'react'

import { Dropdown, type DropdownOption } from '@/components/ui'
import { useApi } from '@/hooks'
import { assetDropdownLabel } from '@/insights/common/assetLabel'
import { AssetTypeFilter, type Asset } from '@/types/dashboard-types'
import { type DataSource } from '@/types/data-source'

import { type AssetDateRange, useAssetCurrentStatus } from './useAssetCurrentStatus'

interface Params {
  companyId?: number
  facilityId?: string
  asset?: string
  assetType?: AssetTypeFilter
  onChange: (assetId: string | undefined) => void
  label?: string
  /** Which back end to list assets from; defaults to Fresh. */
  dataSource?: DataSource
  /** The insight's date selection, which the support-only "has current data" filter uses. */
  dateRange?: AssetDateRange
}

export const AssetDropdownSingle: React.FC<Params> = ({
  asset,
  companyId,
  facilityId,
  assetType,
  onChange,
  label = 'Asset',
  dataSource,
  dateRange,
}) => {
  const { api } = useApi()
  const status = useAssetCurrentStatus(companyId, dateRange, dataSource)
  const [assets, setAssets] = React.useState<Asset[]>([])
  const [placeholder, setPlaceholder] = React.useState('')
  const [lastCompanyId, setLastCompanyId] = useState(companyId)
  const [selected, setSelected] = useState(asset)
  const previousFacilityId = React.useRef(facilityId)

  React.useEffect(() => {
    if (previousFacilityId.current === facilityId) return
    previousFacilityId.current = facilityId
    setSelected(undefined)
    onChange(undefined)
  }, [facilityId])

  React.useEffect(() => {
    if (!api) {
      return
    }
    if (!companyId) {
      setAssets([])
      setPlaceholder('Choose a company to load assets...')
      return
    }
    const fn = async () => {
      setPlaceholder('Loading...')
      if (lastCompanyId !== companyId && assets.length) {
        setAssets([])
        setSelected(undefined)
      }
      setLastCompanyId(companyId)
      const response = await api.get<Asset[]>({
        path: `/fresh/assets?companyId=${companyId}&skip=0&take=10000&deleted=false${
          assetType && assetType !== AssetTypeFilter.All ? `&assetType=${assetType}` : ''
        }${dataSource ? `&dataSource=${dataSource}` : ''}`,
        origin: 'AssetDropdown',
        expiresIn: '2h',
      })
      if (response.error || !response.data) {
        console.error(response.error)
        setPlaceholder('Error loading assets. Contact support.')
        setAssets([])
        setSelected(undefined)
      } else {
        setPlaceholder('Choose an asset...')
        setAssets(response.data)
        if (lastCompanyId !== companyId) {
          setSelected(undefined)
        }
      }
    }
    fn()
  }, [api, companyId, lastCompanyId, assetType])

  const facilityOptions = React.useMemo<DropdownOption[]>(
    () =>
      assets
        .filter((asset) => !facilityId || String(asset.facilityId) === facilityId)
        .map((asset) => ({ label: assetDropdownLabel(asset, dataSource), value: String(asset.id) })),
    [assets, facilityId, dataSource]
  )

  // Memoized: a company can have thousands of assets, and rebuilding them all (icons included)
  // on every render makes typing in the picker lag.
  const { isShown, iconFor } = status
  const visibleOptions = React.useMemo(
    () =>
      facilityOptions
        .filter((o) => isShown(o.value, o.value === selected))
        .map((o) => ({ ...o, icon: iconFor(o.value) })),
    [facilityOptions, isShown, iconFor, selected]
  )

  return (
    <div>
      <label>{label}</label>
      <Dropdown
        options={visibleOptions}
        handleSingleSelectItem={(opt: DropdownOption | null) => {
          setSelected(opt?.value)
          onChange(opt?.value || undefined)
        }}
        selectedItem={visibleOptions.find((o) => o.value === selected) || null}
        placeholder={placeholder}
        animateDots={/^Loading/.test(placeholder)}
      />
      {status.toggle(facilityOptions.map((o) => o.value))}
    </div>
  )
}
